import { Component, inject, ChangeDetectionStrategy } from '@angular/core'
import { Router, RouterLink, RouterLinkActive } from '@angular/router'
import Keycloak from 'keycloak-js'
import { LayoutService } from '../ui/layout.service'
import { HydrationStateService } from '../../core/hydration-state.service'

@Component({
	standalone: true,
	selector: 'app-bottom-nav',
	imports: [RouterLink, RouterLinkActive],
	styles: `
		:host {
			display: contents;
		}

		@keyframes blinkColors {
			0% {
				color: #a855f7;
			}
			25% {
				color: #7c3aed;
			}
			50% {
				color: #6366f1;
			}
			75% {
				color: #a855f7;
			}
			100% {
				color: #7c3aed;
			}
		}
		.blink-icon {
			animation: blinkColors 3s infinite;
		}

		.btn-add {
			width: 3.5rem;
			height: 3.5rem;
			border-radius: 50%;
			background: linear-gradient(135deg, #a855f7, #7c3aed);
			color: white;
			box-shadow: 0 4px 12px rgba(124, 58, 237, 0.4);
			transition:
				transform 0.2s,
				box-shadow 0.2s;
		}
		.btn-add:active {
			transform: scale(0.9);
			box-shadow: 0 2px 6px rgba(124, 58, 237, 0.3);
		}
	`,
	changeDetection: ChangeDetectionStrategy.Eager,
	template: `
		<nav
			class="fixed bottom-0 inset-x-0 z-[996] bg-white border-t border-gray-200 lg:hidden"
			style="padding-bottom: env(safe-area-inset-bottom, 0)">
			<div class="flex items-end justify-around h-16 px-1">
				@if (hydration.hydrated() && keycloakService.authenticated) {
					@if (layoutService.showLanding()) {
						<a
							(click)="goToPanel()"
							class="flex flex-col items-center justify-center gap-0.5 pb-1 text-gray-500
								hover:text-primary active:scale-90 transition-all cursor-pointer select-none w-16">
							<i class="pi pi-th-large text-xl"></i>
							<span class="text-[10px] leading-tight">پنل کاربری</span>
						</a>
					} @else {
						<a
							(click)="goToLanding()"
							class="flex flex-col items-center justify-center gap-0.5 pb-1 text-gray-500
								hover:text-primary active:scale-90 transition-all cursor-pointer select-none w-16">
							<i class="pi pi-home text-xl"></i>
							<span class="text-[10px] leading-tight">صفحه اصلی</span>
						</a>
					}
				} @else {
					<a
						[routerLinkActiveOptions]="{ exact: true }"
						(click)="goToLanding()"
						routerLinkActive="!text-primary"
						class="flex flex-col items-center justify-center gap-0.5 pb-1 text-gray-500
							hover:text-primary active:scale-90 transition-all cursor-pointer select-none w-16">
						<i class="pi pi-home text-xl"></i>
						<span class="text-[10px] leading-tight">خانه</span>
					</a>
				}

				<a
					routerLink="/search"
					routerLinkActive="!text-primary"
					class="flex flex-col items-center justify-center gap-0.5 pb-1 text-gray-500
						hover:text-primary active:scale-90 transition-all cursor-pointer select-none w-16">
					<i class="pi pi-search text-xl"></i>
					<span class="text-[10px] leading-tight">جستجو</span>
				</a>

				<a
					(click)="goToNewAd()"
					class="flex flex-col items-center justify-end gap-0.5 text-gray-500
						cursor-pointer select-none w-16 -mt-5 group">
					<span class="btn-add flex items-center justify-center">
						<i class="pi pi-plus text-2xl"></i>
					</span>
					<span class="text-[10px] leading-tight mt-1">ثبت آگهی</span>
				</a>

				<a
					routerLink="/search"
					routerLinkActive="!text-primary"
					class="flex flex-col items-center justify-center gap-0.5 pb-1 text-gray-500
						hover:text-primary active:scale-90 transition-all cursor-pointer select-none w-16">
					<i class="pi pi-list text-xl"></i>
					<span class="text-[10px] leading-tight">آگهی ها</span>
				</a>

				@if (!hydration.hydrated() || !keycloakService.authenticated) {
					<a
						(click)="login()"
						class="flex flex-col items-center justify-center gap-0.5 pb-1 text-gray-500
							hover:text-primary active:scale-90 transition-all cursor-pointer select-none w-16">
						<i class="pi pi-sign-in text-xl"></i>
						<span class="text-[10px] leading-tight">ورود</span>
					</a>
				} @else {
					<a
						(click)="goToPanel()"
						class="flex flex-col items-center justify-center gap-0.5 pb-1 text-gray-500
							hover:text-primary active:scale-90 transition-all cursor-pointer select-none w-16">
						<i class="pi pi-user text-xl"></i>
						<span class="text-[10px] leading-tight">پروفایل</span>
					</a>
				}
			</div>
		</nav>
	`,
})
export class BottomNavComponent {
	readonly keycloakService = inject(Keycloak)
	readonly layoutService = inject(LayoutService)
	/** گیت رندر همسان سرور/کلاینت برای بخش‌های وابسته به احراز هویت (SSR). */
	readonly hydration = inject(HydrationStateService)
	readonly #router = inject(Router)

	login() {
		void this.keycloakService.login({ redirectUri: window.location.origin + '/login' })
	}

	goToLanding() {
		this.layoutService.showLanding.set(true)
		this.#router.navigate(['/'])
	}

	goToPanel() {
		this.layoutService.showLanding.set(false)
		this.#router.navigate(['/dashboard'])
	}

	goToNewAd() {
		// ابتدا صفحه انتخاب نوع آگهی (فضا / دوره / تجهیزات) نمایش داده می‌شود؛
		// کاربر پس از انتخاب دسته‌بندی وارد ویزارد همان نوع می‌شود.
		if (this.keycloakService.authenticated) {
			this.#router.navigate(['/ads', 'ad'])
		} else {
			void this.keycloakService.login({ redirectUri: window.location.origin + '/ads/ad' })
		}
	}
}
